import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import profileService from '@services/profileService';
import type { Profile, ProfileFeatures } from '@/types/planning';

/**
 * Perfil do usuário nesta aplicação (GET /auth/profile), carregado uma vez depois do login.
 * Não existe equipe nem hierarquia: `isAdmin` só libera as telas de Administração.
 * `features.cac` (liberação do Acervo CAC) vem do backend; o admin sempre tem.
 */
interface ProfileContextType {
  profile: Profile | null;
  isAdmin: boolean;
  /** Módulos liberados (`features` do /auth/profile). `cac` esconde/mostra o menu Acervo CAC. */
  features: ProfileFeatures;
  isLoading: boolean;
  reload: () => Promise<void>;
}


const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const reload = async () => {
    setIsLoading(true);
    try {
      setProfile(await profileService.getProfile());
    } catch (err) {
      // O perfil é complementar: sem ele a tela continua funcionando pelas permissões do token.
      console.warn('Falha ao carregar /auth/profile:', err);
      setProfile(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const value = useMemo<ProfileContextType>(
    () => ({
      profile,
      isAdmin: !!profile?.is_admin,
      // Admin sempre tem o CAC (regra do backend); sem perfil carregado, nada é liberado.
      features: { cac: !!(profile?.features?.cac || profile?.is_admin) },
      isLoading,
      reload,
    }),
    [profile, isLoading],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useProfile(): ProfileContextType {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile deve ser usado dentro de um ProfileProvider');
  }
  return context;
}
